import { useContext } from 'react';

import {
  ADOTViewer,
  canApproveOrRejectADOTTransfer,
  canCancelADOTTransfer,
} from '@atom/components/common/requests/adotRequestPermissions';
import { Button } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetRequestStatus } from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ViewRequestContext from '../ViewRequestContext';

import ADOTApprovalForm from './ADOTApprovalForm';

import classes from './adotView.module.css';

const styles = {
  cancelButton: {
    width: '100%',
    color: colors.brand.red,
    border: `1px solid ${colors.neutral.ash}`,
  },
  rejectButton: {
    width: '48%',
    border: `1px solid ${colors.neutral.ash}`,
  },
  approveButton: {
    width: '48%',
  },
};

type ADOTApprovalControlsProps = {
  viewer: ADOTViewer;
  viewerLoading: boolean;
};

const ADOTApprovalControls = ({
  viewer,
  viewerLoading,
}: ADOTApprovalControlsProps) => {
  const { status, setStatus, setQuantityApproved, activeRequest } =
    useContext(ViewRequestContext);

  const showCancel = canCancelADOTTransfer(activeRequest, viewer);

  // Approve/Reject wait for the live membership check so a user removed from
  // an assigned group never sees the buttons flash in.
  const showApproveReject =
    !viewerLoading && canApproveOrRejectADOTTransfer(activeRequest, viewer);

  const handleApprove = () => {
    setQuantityApproved(activeRequest?.quantityOrdered);
    setStatus(AssetRequestStatus.FULL_APPROVAL);
  };

  return isNilOrEmpty(status) ? (
    <div className={classes.approvalControls}>
      {showCancel && (
        <Button
          style={styles.cancelButton}
          onClick={() => setStatus(AssetRequestStatus.CANCELED)}
          data-cy="adotTransferCancelButton"
        >
          Cancel Request
        </Button>
      )}
      {showApproveReject && (
        <div className={classes.subButtons}>
          <Button
            style={styles.rejectButton}
            onClick={() => setStatus(AssetRequestStatus.REJECTED)}
            data-cy="adotTransferRejectButton"
          >
            Reject
          </Button>
          <Button
            style={styles.approveButton}
            color="primary"
            variant="contained"
            onClick={handleApprove}
            data-cy="adotTransferApproveButton"
          >
            Approve
          </Button>
        </div>
      )}
    </div>
  ) : (
    <ADOTApprovalForm />
  );
};

export default ADOTApprovalControls;
